import React from 'react';
import axios from 'axios';
import { getSession } from 'Vulture/Session';
import { broncoURL, commitHash } from 'Vulture/ENV';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import style from './ChonkyFileBrowser.module.css';

class CreateFileModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            files: [],
            description: '',
            keywords: '',
            isDragging: false,
            fileStatus: {},
            uploadProgress: { completed: 0, total: 0, inProgress: false } 
        };
        
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleFileChange = this.handleFileChange.bind(this);
        this.handleCreateFile = this.handleCreateFile.bind(this);
        this.handleDragEnter = this.handleDragEnter.bind(this);
        this.handleDragLeave = this.handleDragLeave.bind(this);
        this.handleDragOver = this.handleDragOver.bind(this);
        this.handleDrop = this.handleDrop.bind(this);
        this.removeFile = this.removeFile.bind(this);
        this.fileInputRef = React.createRef();
        this.dragCounter = 0;
    }

    handleInputChange(e) {
        this.setState({ [e.target.name]: e.target.value });
    }

    handleFileChange(e) {
        const selectedFiles = Array.from(e.target.files);
        if (selectedFiles.length > 0) {
            const currentLength = this.state.files.length;
            
            const newFileStatus = {...this.state.fileStatus};
            selectedFiles.forEach((_, index) => {
                newFileStatus[currentLength + index] = { status: 'pending', message: '' };
            });
            
            this.setState({
                files: [...this.state.files, ...selectedFiles],
                fileStatus: newFileStatus
            });
        }
    }

    handleDragEnter(e) {
        e.preventDefault();
        e.stopPropagation();
        
        this.dragCounter++;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            this.setState({ isDragging: true });
        }
    }
    
    handleDragLeave(e) {
        e.preventDefault();
        e.stopPropagation();
        
        this.dragCounter--;
        if (this.dragCounter === 0) {
            this.setState({ isDragging: false });
        }
    }
    
    handleDragOver(e) {
        e.preventDefault();
        e.stopPropagation();
    }
    
    handleDrop(e) {
        e.preventDefault();
        e.stopPropagation();
        
        this.setState({ isDragging: false });
        this.dragCounter = 0;
        
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const droppedFiles = Array.from(e.dataTransfer.files);
            const currentLength = this.state.files.length;
            
            const newFileStatus = {...this.state.fileStatus};
            droppedFiles.forEach((_, index) => {
                newFileStatus[currentLength + index] = { status: 'pending', message: '' };
            });
            
            this.setState({
                files: [...this.state.files, ...droppedFiles],
                fileStatus: newFileStatus
            });
            e.dataTransfer.clearData();
        }
    }

    removeFile(index) {
        const updatedFiles = [...this.state.files];
        updatedFiles.splice(index, 1);
        
        const updatedFileStatus = {};
        Object.entries(this.state.fileStatus).forEach(([oldIndex, status]) => {
            const numIndex = parseInt(oldIndex);
            if (numIndex < index) {
                updatedFileStatus[numIndex] = status;
            } else if (numIndex > index) {
                updatedFileStatus[numIndex - 1] = status;
            }
        });
        
        this.setState({ 
            files: updatedFiles,
            fileStatus: updatedFileStatus
        });
    }

    async handleCreateFile() {
        const { files, description, keywords, } = this.state;
        const { currentFolderId, refreshFileBrowserData, app } = this.props;

        if (files.length === 0) {
            alert('Please select at least one file to upload');
            return;
        }

        const filesToUpload = files.filter((_, index) => {
            const status = this.state.fileStatus[index]?.status;
            return status !== 'success'; 
        });
        
        if (filesToUpload.length === 0) {
            alert('All files have already been uploaded successfully');
            return;
        }
        
        this.setState({
            uploadProgress: {
                completed: 0,
                total: filesToUpload.length,
                inProgress: true
            }
        });
        
        app.setState({ loading: true });
        
        try {
            const session = getSession();
            let url;
            if (currentFolderId === 'root') {
                url = `${broncoURL}/api/folder/1/file`;
            } else {
                const backendFolderId = currentFolderId.replace('node_', '');
                url = `${broncoURL}/api/folder/${backendFolderId}/file`;
            }

            for (let i = 0; i < files.length; i++) {
                if (this.state.fileStatus[i]?.status === 'success') {
                    continue;
                }
                
                const file = files[i];
                
                this.setState(prevState => ({
                    fileStatus: {
                        ...prevState.fileStatus,
                        [i]: { status: 'uploading', message: 'Uploading...' }
                    }
                }));
                
                try {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('file_name', file.name);
                    if (description) {
                        formData.append('description', description)
                    } else {
                        formData.append('description', file.name);
                    }
                    if (keywords) formData.append('keywords', keywords);

                    await axios.post(url, formData, {
                        headers: {
                            'X-Auth-Session': session.session,
                            'X-Spark-API-version': commitHash,
                        }
                    });
                    
                    this.setState(prevState => ({
                        fileStatus: {
                            ...prevState.fileStatus,
                            [i]: { status: 'success', message: 'Upload complete' }
                        },
                        uploadProgress: {
                            ...prevState.uploadProgress,
                            completed: prevState.uploadProgress.completed + 1
                        }
                    }));
                } catch (err) {
                    this.setState(prevState => ({
                        fileStatus: {
                            ...prevState.fileStatus,
                            [i]: { 
                                status: 'error', 
                                message: err.response?.data?.message || err.message || 'Upload failed'
                            }
                        }
                    }));
                }
            }
            
            await refreshFileBrowserData();
            
            this.setState(prevState => ({
                uploadProgress: {
                    ...prevState.uploadProgress,
                    inProgress: false
                }
            }));
            
            const allSuccess = Object.values(this.state.fileStatus).every(
                status => status.status === 'success'
            );
            
            if (allSuccess) {
                app.closeModal();
                this.setState({
                    files: [],
                    description: '',
                    keywords: '',
                    fileStatus: {}
                });
            }
        } catch (err) {
            console.error('File upload error:', err);
            alert(`Error uploading files: ${err.response?.data?.message || err.message}`);
            this.setState(prevState => ({
                uploadProgress: {
                    ...prevState.uploadProgress,
                    inProgress: false
                }
            }));
        } finally {
            app.setState({ loading: false });
        }
    }

    getStatusIcon(status) {
        switch (status) {
            case 'pending':
                return <CustomIcon icon='file' />;
            case 'uploading':
                return <CustomIcon icon='cloud-upload-alt' />;  
            case 'success':
                return <CustomIcon icon='check-circle'/>;
            case 'error':
                return <CustomIcon icon='exclamation-circle' />;
            default:
                return <CustomIcon icon='file' />;
        }
    }

    render() {
        const { isDragging, files, fileStatus, uploadProgress } = this.state;
        
        const pendingFiles = [];
        const successFiles = [];
        const errorFiles = [];
        
        files.forEach((file, index) => {
            const status = fileStatus[index]?.status || 'pending';
            if (status === 'success') {
                successFiles.push({ file, index, status: fileStatus[index] });
            } else if (status === 'error') {
                errorFiles.push({ file, index, status: fileStatus[index] });
            } else {
                pendingFiles.push({ file, index, status: fileStatus[index] });
            }
        });
        
        const renderFileItem = (fileInfo) => (
            <li key={fileInfo.index} className={`${style.fileItem} ${fileInfo.status?.status === 'error' ? style.errorItem : ''}`}>
                {this.getStatusIcon(fileInfo.status?.status)}
                <span>{fileInfo.file.name}</span>
                {fileInfo.status?.status === 'error' && (
                    <span className={style.errorMessage}>
                        {fileInfo.status.message}
                    </span>
                )}
                <button 
                    className={style.removeFile} 
                    onClick={(e) => {
                        e.stopPropagation();
                        this.removeFile(fileInfo.index);
                    }}
                    disabled={fileInfo.status?.status === 'uploading'}
                >
                    <CustomIcon icon='times' />
                </button>
            </li>
        );
        
        return (
            <div className={style.chonkyModal}>
                <div 
                    className={`${style.formGroup} ${style.fileDropArea} ${isDragging ? style.dragging : ''}`}
                    onDragEnter={this.handleDragEnter}
                    onDragLeave={this.handleDragLeave}
                    onDragOver={this.handleDragOver}
                    onDrop={this.handleDrop}
                    onClick={() => this.fileInputRef.current.click()}
                >
                    <div className={style.fileDropContent}>
                        {files.length > 0 ? (
                            <div className={style.selectedFiles}>
                                <p>{files.length} file(s) selected</p>
                                
                                {uploadProgress.inProgress && (
                                    <div className={style.uploadProgressContainer}>
                                        <div className={style.uploadProgressBar}>
                                            <div 
                                                className={style.uploadProgressFill} 
                                                style={{
                                                    width: `${(uploadProgress.completed / uploadProgress.total) * 100}%`
                                                }}
                                            ></div>
                                        </div>
                                        <p className={style.uploadProgressText}>
                                            Uploading: {uploadProgress.completed} of {uploadProgress.total} complete
                                        </p>
                                    </div>
                                )}
                                
                                {pendingFiles.length > 0 && (
                                    <>
                                        <h4>Pending</h4>
                                        <ul className={style.fileList}>
                                            {pendingFiles.map(renderFileItem)}
                                        </ul>
                                    </>
                                )}
                                
                                {errorFiles.length > 0 && (
                                    <>
                                        <h4>Failed</h4>
                                        <ul className={style.fileList}>
                                            {errorFiles.map(renderFileItem)}
                                        </ul>
                                    </>
                                )}
                                
                                {successFiles.length > 0 && (
                                    <>
                                        <h4>Successful</h4>
                                        <ul className={style.fileList}>
                                            {successFiles.map(renderFileItem)}
                                        </ul>
                                    </>
                                )}
                            </div>
                        ) : (
                            <>
                                <CustomIcon icon='upload' className={style.uploadIcon} />
                                <p>{isDragging ? 'Drop files here' : 'Drag and drop files here or click to browse'}</p>
                            </>
                        )}
                        <input
                            type="file"
                            onChange={this.handleFileChange}
                            ref={this.fileInputRef}
                            style={{ display: 'none' }}
                            multiple
                        />
                    </div>
                </div>

                <div className={style.formGroup}>
                    <label>
                        Description
                        <textarea
                            name="description"
                            value={this.state.description}
                            onChange={this.handleInputChange}
                            placeholder="File description (optional)"
                        />
                    </label>
                </div>

                <div className={style.formGroup}>
                    <label>
                        Keywords
                        <input
                            type="text"
                            name="keywords"
                            value={this.state.keywords}
                            onChange={this.handleInputChange}
                            placeholder="Comma-separated keywords (optional)"
                        />
                    </label>
                </div>

                <button
                    onClick={this.handleCreateFile}
                    className={`button`}
                    disabled={
                        files.length === 0 || 
                        Object.values(fileStatus).some(status => status?.status === 'uploading') ||
                        Object.values(fileStatus).every(status => status?.status === 'success')
                    }
                >
                    <CustomIcon icon='upload' /> 
                    {errorFiles.length > 0 ? 'Retry Failed Uploads' : `Upload File${files.length > 1 ? 's' : ''}`}
                </button>
            </div>
        );
    }
}

export default CreateFileModal;